import { z } from "zod";

/** better-auth owns the user/session/account/verification tables directly; this is only the
 * user-facing shape other new contracts (friendship, group, tastingSession) reference. */
export const UserProfile = z.object({
  id: z.string().min(1),
  /** Optional so social views of other people can stop carrying it: the PWA ships this first,
   * the edge stops sending it a release later, because installed PWAs parse every response. */
  email: z.email().optional(),
  /** The searchable name others find someone by; optional, as installed PWAs parse profiles from
   * before it was sent. */
  handle: z.string().min(1).nullable().optional(),
  displayName: z.string().min(1).max(60),
  avatarUrl: z.url().nullable(),
  /** Gates broadcast.ts's "everyone" target -- a sender-controlled visibility flag, default false. */
  isPublic: z.boolean(),
  /** Gates GET /v1/friends/search -- human override #3 (brief.md): opt-out, defaulted to true at
   * the DB/account-creation layer (apps/edge's better-auth config and migration 0016), not here --
   * optional at the schema level purely so this additive field doesn't force every existing
   * UserProfile-shaped fixture across the workspace to be touched in the same change. */
  discoverable: z.boolean().optional(),
  createdAt: z.iso.datetime(),
});
export type UserProfile = z.infer<typeof UserProfile>;

export const OAuthProvider = z.enum(["google", "microsoft", "instagram", "tiktok"]);
export type OAuthProvider = z.infer<typeof OAuthProvider>;

/** GET /v1/config reads this to grey out a not-yet-reviewed provider row, instead of a client
 * discovering it only on a redirect failure. */
export const OAuthProviderAvailability = z.partialRecord(OAuthProvider, z.boolean());
export type OAuthProviderAvailability = z.infer<typeof OAuthProviderAvailability>;

/** PATCH /v1/account -- the About screen's visibility toggle; every field optional, only
 * isPublic exists today, further account fields extend this contract-first (AGENTS.md). */
export const PatchAccountRequest = z.object({
  isPublic: z.boolean().optional(),
  /** The About screen's "Anderen kunnen je vinden" toggle (human override #3) -- off removes the
   * account from every other searcher's results immediately. */
  discoverable: z.boolean().optional(),
  /** Raw text: the edge folds and checks it with `checkDisplayName`, whose result may be shorter. */
  displayName: z.string().max(200).optional(),
  /** Raw text: `checkHandle` lowercases and judges it; one change per 30 days. */
  handle: z.string().max(64).optional(),
  /** Only ever null: remove the picture. A new one goes up through PUT /v1/account/avatar. */
  avatarUrl: z.null().optional(),
});
export type PatchAccountRequest = z.infer<typeof PatchAccountRequest>;
